import { useEffect } from 'react'

import Empty from '../../assets/icons/Empty'
import warningIcon from '../../assets/icons/warningIcon.svg'
import CustomTable from '../Table'
import CustomTableBody from '../Table/TableBody'
import CustomTableData from '../Table/TableData'
import CustomTableDataCTA from '../Table/TableDataCTA'
import CustomTableHead from '../Table/TableHead'
import CustomTableRow from '../Table/TableRow'
import { calculateValidityDate } from '../../utils/formatDate'
import { ResidentDTO, ResidentMembershipResponse } from '../../interfaces/resident'

interface ResidentDetailsProperty {
  id: string
  property: string
  access: string
  levyStatus: string
  validity: string
  propertyMemberId: string
}

interface ResidentDetailsProps {
  resident: ResidentDTO
  membershipData?: ResidentMembershipResponse
  membershipLoading: boolean
  membershipError?: unknown
  onAccessControl: (property: { id: string; property: string }) => void
  onPaymentHistory: (property: { id: string; property: string }) => void
  onAccessHistory: (property: { id: string; property: string }) => void
}

const ResidentDetailsTable = ({
  resident,
  membershipData,
  membershipLoading,
  membershipError,
  onAccessControl,
  onPaymentHistory,
  onAccessHistory,
}: ResidentDetailsProps) => {
  const communityMemberId = resident?.id

  const tableHeadData = ['Property', 'Access', 'Levy Status', 'Validity', '']


  const getLevyStatusClass = (status: string) => {
    if (status === 'Paid') return 'text-green-600 font-medium'
    if (status === 'Unpaid') return 'text-red-600 font-medium'
    return ''
  }

  const user = resident?.resident || {}

  const mappedProperties: ResidentDetailsProperty[] =
    membershipData?.results?.map((membership) => {
      const accessStatus = membership.propertyMemberAccess?.status

      return {
        id: membership.property?._id || membership._id,

        property: membership.property?.address || 'NIL',

        access:
          accessStatus?.toLowerCase() === 'enabled'
            ? 'Enabled'
            : accessStatus?.toLowerCase() === 'disabled'
              ? 'Disabled'
              : accessStatus?.toLowerCase() === 'indefinite'
                ? 'Enabled Indefinitely'
                : 'No access',

        levyStatus:
          accessStatus?.toLowerCase() === 'enabled'
            ? 'Paid'
            : accessStatus?.toLowerCase() === 'disabled'
              ? 'Unpaid'
              : accessStatus?.toLowerCase() === 'indefinite'
                ? 'Paid'
                : '-',

        validity: calculateValidityDate(
          membership.propertyMemberAccess?.enabledAt,
          membership.duration
        ),

        propertyMemberId: membership.propertyMemberAccess?.memberId || '',
      }
    }) || []

  return (
    <section className="flex flex-col gap-6 bg-[#F8F9FA] p-3 sm:gap-10 sm:p-5">
      <div>
        <div className="mb-5 flex flex-col space-y-1.5 sm:mb-6 sm:space-y-2">
          <p className="font-medium">
            {user.firstName} {user.lastName}
          </p>

          <p className="text-sm font-medium">{user.phone || 'NIL'}</p>
        </div>

      
          <CustomTable className="min-w-[650px]">
            <CustomTableHead
              dataList={tableHeadData}
              cellClassName="!text-left !px-2 first:!pl-5 last:!pl-5"
            />

            {membershipLoading ? (
              <tbody>
                <tr>
                  <td colSpan={tableHeadData.length}>
                    <div className="flex h-[300px] w-full items-center justify-center">
                      <p className="text-sm font-semibold">Loading...</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : membershipError ? (
              <tbody>
                <tr>
                  <td colSpan={tableHeadData.length}>
                    <div className="flex h-[300px] w-full items-center justify-center">
                      <p className="text-sm font-semibold text-red-600">
                        Failed to load resident properties
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : mappedProperties.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={tableHeadData.length}>
                    <div className="flex h-[300px] w-full flex-col items-center justify-center gap-4">
                      <Empty />
                      <p className="text-sm font-semibold">No results found</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <CustomTableBody>
                {mappedProperties.map((item, index) => (
                  <CustomTableRow
                    key={item.id}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-[#F8F9FA]'}
                  >
                    <CustomTableData
                      dataLabel="Property"
                      text={item.property}
                      className="max-w-[200px] truncate"
                    />

                    <CustomTableData dataLabel="Access" text={item.access} />

                    <CustomTableData
                      dataLabel="Levy Status"
                      text={item.levyStatus}
                      className={getLevyStatusClass(item.levyStatus)}
                    />

                    <CustomTableData dataLabel="Validity" text={item.validity} />

                    <CustomTableDataCTA dataLabel="More" text="...">
                      <p
                        onClick={() =>
                          onAccessControl({
                            id: item.id,
                            property: item.property,
                          })
                        }
                        className="border-t p-3 text-sm whitespace-nowrap hover:bg-gray-200"
                      >
                        Access Control
                      </p>

                      <p
                        className="w-60 border-t p-3 text-sm whitespace-normal hover:bg-gray-200"
                        onClick={() =>
                          onPaymentHistory({
                            id: item.id,
                            property: item.property,
                          })
                        }
                      >
                        View payment history
                      </p>

                      <p
                        onClick={() =>
                          onAccessHistory({
                            id: item.id,
                            property: item.property,
                          })
                        }
                        className="border-t p-3 text-sm whitespace-nowrap hover:bg-gray-200"
                      >
                        View Access history
                      </p>
                    </CustomTableDataCTA>
                  </CustomTableRow>
                ))}
              </CustomTableBody>
            )}
          </CustomTable>
      </div>

      <div className="flex items-start gap-2.5 rounded-lg bg-[#E9ECEF] px-3 py-2 sm:items-center sm:px-4">
        <img src={warningIcon} alt="warning_icon" className="mt-0.5 shrink-0 sm:mt-0" />

        <div>
          <p className="font-medium">
            Automatic levy enforcement is enabled for this estate.
          </p>

          <p className="text-[13px] font-normal text-[#6C757D]">
            Residents may be disabled automatically if levy expires
          </p>
        </div>
      </div>
    </section>
  )
}

export default ResidentDetailsTable
